import React, { useState } from 'react';
import { Mail, Users, CheckCircle, User, Briefcase } from 'lucide-react';

const ContactInfo = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [brokerage, setBrokerage] = useState('');
  const [teamSize, setTeamSize] = useState('solo');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const endpoint = 'https://app.getsortio.com/api/sortio-waitlist/';

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          waitlist_type: 'realtors',
          name: brokerage ? `${name} (${brokerage})` : name,
          email,
          company_size: teamSize,
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setSubmitted(true);
        setName('');
        setEmail('');
        setBrokerage('');
        setTeamSize('solo');
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Submission error:', errorData);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setSubmitStatus('error');
    }

    setIsSubmitting(false);
  };

  return (
    <section id="contact" className="my-20">
      <div className="text-center mb-12">
        <h2 className="text-3xl sm:text-4xl font-semibold mb-4 tracking-tight text-apple-50">
          Request a <span className="apple-gradient-text">Demo</span>
        </h2>
        <p className="text-apple-300 text-lg font-light max-w-2xl mx-auto">
          Tell us about your business and we'll show you Sortio filing a real transaction folder.
        </p>
      </div>

      <div className="bg-apple-700/30 backdrop-blur-md p-8 sm:p-10 rounded-3xl border border-apple-600/30 relative overflow-hidden max-w-2xl mx-auto">
        <div className="absolute -left-40 -top-40 w-80 h-80 rounded-full bg-sortio-primary/10 opacity-30 blur-[100px]" />
        <div className="absolute -right-40 -bottom-40 w-80 h-80 rounded-full bg-sortio-secondary/10 opacity-30 blur-[100px]" />

        {!submitted ? (
          <form onSubmit={handleSubmit} className="relative z-10">
            <div className="mb-5">
              <label htmlFor="name" className="block text-sm font-medium mb-2 text-apple-100">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-4 top-3.5 text-apple-400" size={16} />
                <input
                  type="text"
                  id="name"
                  className="w-full px-4 py-3 pl-10 rounded-xl bg-apple-800/70 border border-apple-600/60 text-apple-50 focus:outline-none focus:ring-2 focus:ring-sortio-primary/50 focus:border-sortio-primary/50 transition-all"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="mb-5">
              <label htmlFor="email" className="block text-sm font-medium mb-2 text-apple-100">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-3.5 text-apple-400" size={16} />
                <input
                  type="email"
                  id="email"
                  className="w-full px-4 py-3 pl-10 rounded-xl bg-apple-800/70 border border-apple-600/60 text-apple-50 focus:outline-none focus:ring-2 focus:ring-sortio-primary/50 focus:border-sortio-primary/50 transition-all"
                  placeholder="you@brokerage.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="mb-5">
              <label htmlFor="brokerage" className="block text-sm font-medium mb-2 text-apple-100">
                Brokerage <span className="text-apple-400 text-xs">(Optional)</span>
              </label>
              <div className="relative">
                <Briefcase className="absolute left-4 top-3.5 text-apple-400" size={16} />
                <input
                  type="text"
                  id="brokerage"
                  className="w-full px-4 py-3 pl-10 rounded-xl bg-apple-800/70 border border-apple-600/60 text-apple-50 focus:outline-none focus:ring-2 focus:ring-sortio-primary/50 focus:border-sortio-primary/50 transition-all"
                  placeholder="Coldwell Banker, Compass, eXp, etc."
                  value={brokerage}
                  onChange={(e) => setBrokerage(e.target.value)}
                />
              </div>
            </div>

            <div className="mb-7">
              <label htmlFor="teamSize" className="block text-sm font-medium mb-2 text-apple-100">
                Team Size
              </label>
              <div className="relative">
                <Users className="absolute left-4 top-3.5 text-apple-400" size={16} />
                <select
                  id="teamSize"
                  className="w-full px-4 py-3 pl-10 rounded-xl bg-apple-800/70 border border-apple-600/60 text-apple-50 focus:outline-none focus:ring-2 focus:ring-sortio-primary/50 focus:border-sortio-primary/50 transition-all appearance-none"
                  value={teamSize}
                  onChange={(e) => setTeamSize(e.target.value)}
                  required
                >
                  <option value="solo">Solo agent</option>
                  <option value="2-10">2-10</option>
                  <option value="11-50">11-50</option>
                  <option value="50+">50+</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="h-5 w-5 text-apple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            <button
              type="submit"
              className="w-full bg-gradient-to-r from-sortio-primary to-sortio-primary/80 text-white font-medium px-8 py-4 text-base rounded-xl shadow-lg shadow-sortio-primary/20 hover:shadow-sortio-primary/30 hover:brightness-110 transition-all duration-300 flex items-center justify-center group"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  Request a Demo
                  <svg className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </>
              )}
            </button>

            {submitStatus === 'error' && (
              <div className="mt-4 p-3 bg-gradient-to-r from-red-500/80 to-red-600/60 text-white rounded-lg flex items-center text-sm">
                <svg className="w-5 h-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span>Something went wrong. Please try again or email marcus@getsortio.com.</span>
              </div>
            )}

            <p className="text-apple-400 text-xs text-center mt-4">
              By submitting, you agree to be contacted about Sortio. We respect your privacy.
            </p>
          </form>
        ) : (
          <div className="text-center py-8 relative z-10">
            <div className="w-20 h-20 rounded-full bg-sortio-secondary/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-sortio-secondary" />
            </div>
            <h3 className="text-2xl font-medium mb-3 text-apple-50">Thank you</h3>
            <p className="text-apple-300">
              We received your request. A member of our team will reach out shortly to schedule a
              demo with your transaction folder.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ContactInfo;
